export const WORK_KINDS = ["epic", "story", "task"];
export const FIBONACCI_POINTS = [1, 2, 3, 5, 8, 13, 21];

export const WORK_KIND_LABELS = {
  epic: "Epic",
  story: "Story",
  task: "Task",
};

const UPCOMING_LANE_RE = /^(future|later|upcoming|someday)/i;

export function workKindOf(post) {
  const raw = String(post?.work_kind ?? post?.workKind ?? "task").toLowerCase();
  if (raw === "epic" || raw === "story" || raw === "task") return raw;
  return "task";
}

export function workKindLabel(postOrKind) {
  const kind =
    typeof postOrKind === "string" ? postOrKind : workKindOf(postOrKind);
  return WORK_KIND_LABELS[kind] || "Task";
}

export function parentIdOf(post) {
  const id = post?.parent_id ?? post?.parentId ?? post?.parent?.postId ?? null;
  const num = Number(id);
  return Number.isFinite(num) && num > 0 ? num : null;
}

export function parentTitleOf(post) {
  const title = post?.parent?.title;
  return title && String(title).trim() ? String(title).trim() : null;
}

export function storyPointsOf(post) {
  const raw = post?.story_points ?? post?.storyPoints;
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function childCountsOf(post) {
  const raw = post?.child_counts ?? post?.childCounts;
  if (!raw || typeof raw !== "object") return null;
  const total = Number(raw.total);
  const done = Number(raw.done);
  if (!Number.isFinite(total) || total < 1) return null;
  return {
    total,
    done: Number.isFinite(done) ? done : 0,
  };
}

export function postIdOfWork(post) {
  const id = post?.post_id ?? post?.postId;
  const num = Number(id);
  return Number.isFinite(num) && num > 0 ? num : null;
}

export function cardLaneOf(post) {
  return post?.kanban_status ?? post?.kanbanStatus ?? null;
}

/** Parse due/start; date-only strings use noon local to avoid UTC-midnight shift. */
export function parseWorkDate(raw) {
  if (!raw) return null;
  if (raw instanceof Date) {
    return Number.isFinite(raw.getTime()) ? raw : null;
  }
  const s = String(raw);
  const d = new Date(s.includes("T") ? s : `${s}T12:00:00`);
  return Number.isFinite(d.getTime()) ? d : null;
}

export function cardStartsAt(post) {
  return parseWorkDate(post?.starts_at ?? post?.startsAt);
}

export function cardDueAt(post) {
  return parseWorkDate(post?.due_date ?? post?.dueDate);
}

/** Due date is in the past and the card is not on the completed-work lane. */
export function isCardOverdue(card, completedWorkLane = null) {
  const due = cardDueAt(card);
  if (!due) return false;
  if (completedWorkLane && cardLaneOf(card) === completedWorkLane) return false;
  return due.getTime() < Date.now();
}

function startOfLocalDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Epic time horizon for board strips.
 * @returns {{ horizon: "now" | "upcoming" | "done", overdue: boolean }}
 */
export function epicHorizonOf(card, { completedWorkLane = null, today = new Date() } = {}) {
  const lane = cardLaneOf(card);
  const todayStart = startOfLocalDay(today);
  const due = cardDueAt(card);
  const start = cardStartsAt(card);
  const isDone = Boolean(completedWorkLane && lane === completedWorkLane);
  const overdue = Boolean(
    !isDone && due && startOfLocalDay(due).getTime() < todayStart.getTime()
  );

  if (isDone) {
    return { horizon: "done", overdue: false };
  }
  if (start && startOfLocalDay(start).getTime() > todayStart.getTime()) {
    return { horizon: "upcoming", overdue };
  }
  if (
    !start &&
    typeof lane === "string" &&
    UPCOMING_LANE_RE.test(lane.trim())
  ) {
    return { horizon: "upcoming", overdue };
  }
  return { horizon: "now", overdue };
}

/**
 * Ancestors from farthest (epic) to immediate parent.
 * Falls back to `card.parent` snapshot when the parent is not in `cards`.
 */
export function parentChainOf(card, cards) {
  const byId = new Map();
  for (const c of cards || []) {
    const id = postIdOfWork(c);
    if (id) byId.set(id, c);
  }
  const chain = [];
  const seen = new Set();
  let pid = parentIdOf(card);
  let current = card;
  while (pid && !seen.has(pid)) {
    seen.add(pid);
    const parent = byId.get(pid);
    if (parent) {
      chain.unshift(parent);
      current = parent;
      pid = parentIdOf(parent);
      continue;
    }
    const snap = current?.parent;
    const snapId = Number(snap?.postId ?? snap?.post_id);
    if (snap && snapId === pid) {
      chain.unshift({
        post_id: pid,
        postId: pid,
        title: snap.title,
        work_kind: snap.workKind ?? snap.work_kind ?? "task",
        workKind: snap.workKind ?? snap.work_kind ?? "task",
        team_id: snap.teamId ?? snap.team_id ?? null,
        teamId: snap.teamId ?? snap.team_id ?? null,
        team_name: snap.team_name ?? snap.teamName ?? null,
      });
    }
    break;
  }
  return chain;
}

/** Direct + story-nested children of an epic (from an in-memory board list). */
export function isUnderEpic(card, epicId, cards) {
  const pid = parentIdOf(card);
  if (!pid) return false;
  if (pid === epicId) return true;
  const parent = (cards || []).find((c) => postIdOfWork(c) === pid);
  if (!parent) return false;
  return parentIdOf(parent) === epicId;
}

/** Direct children, or the full epic tree (stories + nested tasks). */
export function isUnderWorkParent(card, parentId, cards) {
  const pid = Number(parentId);
  if (!Number.isFinite(pid) || pid < 1) return false;
  if (parentIdOf(card) === pid) return true;
  const parent = (cards || []).find((c) => postIdOfWork(c) === pid);
  if (parent && workKindOf(parent) === "epic") {
    return isUnderEpic(card, pid, cards);
  }
  return false;
}

export function linkedWorkSet(focusCard, cards) {
  const focusId = postIdOfWork(focusCard);
  if (!focusId) return [];
  const pool = cards || [];
  return pool.filter((c) => {
    const id = postIdOfWork(c);
    if (id === focusId) return true;
    return isUnderWorkParent(c, focusId, pool);
  });
}

const KIND_ORDER = { epic: 0, story: 1, task: 2 };

export function sortLinkedWork(cards) {
  return [...(cards || [])].sort((a, b) => {
    const ka = KIND_ORDER[workKindOf(a)] ?? 9;
    const kb = KIND_ORDER[workKindOf(b)] ?? 9;
    if (ka !== kb) return ka - kb;
    const ta = String(a?.title || "");
    const tb = String(b?.title || "");
    return ta.localeCompare(tb);
  });
}
